/**
 * Tarjeta del Inicio de la central: pedidos de matrícula de las sucursales
 * esperando respuesta. Solo con el flag `modificar` de pedidomatriculas (el
 * mismo que exige la bandeja en el backend). Sin Firebase: el contador se
 * consulta al entrar y cada minuto.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ButtonBase from "@mui/material/ButtonBase";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP } from "components/financepro";
import { useApi } from "lib/api";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { PROGRAMA_PEDIDOS } from "modules/matriculas/pedidos";

const CADA_MS = 60 * 1000;

export default function AvisoPedidosMatricula() {
  const [{ roleDetalles }] = useMaterialUIController();
  const puede = puedeAccionPrograma(roleDetalles, PROGRAMA_PEDIDOS, "modificar");
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const navigate = useNavigate();
  const [conteo, setConteo] = useState({ porrevisar: 0, porgestionar: 0, pordespachar: 0 });
  const previo = useRef(null);

  const consultar = useCallback(async () => {
    const { ok, data } = await post("/api/pedidomatricula_resumen", {});
    if (!ok) return;
    const n = data.porrevisar || 0;
    // Aviso solo cuando aumenta (no al cargar).
    if (previo.current !== null && n > previo.current) {
      showAlert({
        message:
          n - previo.current === 1
            ? "Una sucursal pidió una matrícula"
            : `Las sucursales pidieron ${n - previo.current} matrículas`,
        color: "info",
      });
    }
    previo.current = n;
    setConteo(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  useEffect(() => {
    if (!puede) return undefined;
    consultar();
    const id = setInterval(consultar, CADA_MS);
    return () => clearInterval(id);
  }, [puede, consultar]);

  if (!puede) return null;
  const pendientes = conteo.porrevisar || 0;
  const hay = pendientes > 0;
  const resto = [
    conteo.porgestionar ? `${conteo.porgestionar} aceptado(s) sin tramitar` : "",
    conteo.pordespachar ? `${conteo.pordespachar} en la central por enviar` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <ButtonBase
      onClick={() => navigate("/matriculas/pedidos?vista=bandeja")}
      sx={{
        mb: 4,
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.6,
        p: 2,
        borderRadius: 2.5,
        textAlign: "left",
        bgcolor: hay ? FP.color.warningSoft : FP.color.cardBg,
        border: `1px solid ${hay ? "#FCD34D" : "#E5E9F0"}`,
        boxShadow: FP.shadow.soft,
        transition: "all .15s ease",
        "&:hover": {
          borderColor: hay ? FP.color.warning : FP.color.navy,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: hay ? FP.color.warning : `${FP.color.navy}12`,
          color: hay ? "#FFFFFF" : FP.color.navy,
          flexShrink: 0,
        }}
      >
        <BadgeOutlinedIcon sx={{ fontSize: 24 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: FP.color.textPrimary }}>
          Pedidos de matrícula
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: hay ? "#92400E" : FP.color.textSecondary }}>
          {pendientes === 0 && "Ninguna sucursal está esperando respuesta"}
          {pendientes === 1 && "1 pedido de sucursal esperando respuesta"}
          {pendientes > 1 && `${pendientes} pedidos de sucursales esperando respuesta`}
          {resto && ` · ${resto}`}
        </Typography>
      </Box>
      {hay && (
        <Box
          sx={{
            minWidth: 30,
            height: 30,
            px: 1,
            borderRadius: 999,
            bgcolor: FP.color.warning,
            color: "#FFFFFF",
            fontSize: "0.85rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {pendientes > 99 ? "99+" : pendientes}
        </Box>
      )}
      <ChevronRightRoundedIcon sx={{ color: FP.color.textMuted }} />
    </ButtonBase>
  );
}
